/**
 * Founder product health insights — counts and safe metadata only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  ClipboardList,
  Heart,
  Loader2,
  Mail,
  Server,
  Shield,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getApiBase } from '../utils/apiBase';
import { isFounderAdmin } from '../utils/adminAccess';
import { humanizeError } from '../utils/errorHandling';

const API = getApiBase();
const PERIODS = [
  { id: '24h', label: '24h' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
];

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-4">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 font-serif text-2xl font-light text-foreground">{value ?? '—'}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-2xl border border-border/60 bg-card/50 p-5 sm:p-6">
      <div className="mb-4 flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 text-muted-foreground" aria-hidden /> : null}
        <h2 className="font-serif text-lg font-light text-foreground">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function StatusBadge({ status }) {
  const normalized = (status || 'unknown').toLowerCase();
  const variant =
    normalized === 'healthy' ? 'default' : normalized === 'degraded' ? 'secondary' : 'destructive';
  return (
    <Badge variant={variant} className="rounded-full text-[10px] uppercase tracking-wide">
      {status || 'unknown'}
    </Badge>
  );
}

function SimpleTable({ columns, rows, emptyMessage }) {
  if (!rows?.length) {
    return <p className="text-sm text-muted-foreground">{emptyMessage || 'No records yet.'}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-left text-sm">
        <thead>
          <tr className="border-b border-border/50 text-[10px] uppercase tracking-wide text-muted-foreground">
            {columns.map((col) => (
              <th key={col.key} className="px-2 py-2 font-medium">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.id || idx} className="border-b border-border/30 last:border-0">
              {columns.map((col) => (
                <td key={col.key} className="px-2 py-2 align-top text-foreground/85">
                  {col.render ? col.render(row) : row[col.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminInsightsPage() {
  const { user, loading: authLoading } = useAuth();
  const [period, setPeriod] = useState('7d');
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!isFounderAdmin(user)) return;
    setLoading(true);
    setError(null);
    try {
      const [insightsRes, dbRes, emailRes, workerRes, storageRes] = await Promise.all([
        axios.get(`${API}/admin/insights`, { params: { period } }),
        axios.get(`${API}/health/database`).catch(() => ({ data: { status: 'unknown' } })),
        axios.get(`${API}/health/email`).catch(() => ({ data: { status: 'unknown' } })),
        axios.get(`${API}/health/worker`).catch(() => ({ data: { status: 'unknown' } })),
        axios.get(`${API}/health/storage`).catch(() => ({ data: {} })),
      ]);
      setData(insightsRes.data);
      setHealth({
        database: dbRes.data,
        email: emailRes.data,
        worker: workerRes.data,
        storage: storageRes.data,
      });
    } catch (err) {
      setError(humanizeError(err));
    } finally {
      setLoading(false);
    }
  }, [period, user]);

  useEffect(() => {
    load();
  }, [load]);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isFounderAdmin(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  const overview = data?.overview || {};
  const funnel = data?.funnel || {};
  const conversions = data?.funnel_conversions || {};
  const reliability = data?.reliability || {};
  const jobs = data?.jobs || {};
  const memorial = data?.memorial_bridge || {};
  const emailHealth = data?.email_health || {};
  const storageHealth = data?.storage_health || {};
  const events = data?.events || {};
  const signals = data?.decision_signals || [];

  return (
    <div className="min-h-screen bg-background" data-testid="admin-insights-page">
      <header className="border-b border-border/40 bg-card/40">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Button type="button" variant="ghost" size="sm" className="rounded-full" asChild>
              <Link to="/dashboard">
                <ArrowLeft className="mr-1 h-4 w-4" />
                Dashboard
              </Link>
            </Button>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                Internal · Founder
              </p>
              <h1 className="font-serif text-xl font-light text-foreground">Product health insights</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="rounded-full" asChild>
              <Link to="/admin/product-requests">Product requests</Link>
            </Button>
            {PERIODS.map((p) => (
              <Button
                key={p.id}
                type="button"
                size="sm"
                variant={period === p.id ? 'default' : 'outline'}
                className="rounded-full"
                onClick={() => setPeriod(p.id)}
              >
                {p.label}
              </Button>
            ))}
            <Button type="button" size="sm" variant="ghost" className="rounded-full" onClick={load} disabled={loading}>
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-4 py-8 sm:px-6">
        <div className="rounded-xl border border-amber-200/60 bg-amber-50/50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-100">
          <div className="flex items-start gap-2">
            <Shield className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <p>
              Safe aggregates only — no story text, vault notes, documents, or DNA. User emails appear only in failure
              rows where already admin-safe.
            </p>
          </div>
        </div>

        <nav
          className="grid gap-3 sm:grid-cols-2"
          aria-label="Founder admin tools"
          data-testid="admin-tools-nav"
        >
          <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
            <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">Current</p>
            <p className="mt-1 font-serif text-base font-light text-foreground">Product health insights</p>
            <p className="mt-1 text-xs text-muted-foreground">Platform counts and safe operational metadata.</p>
          </div>
          <Link
            to="/admin/product-requests"
            className="group rounded-xl border border-border/60 bg-card/50 px-4 py-3 transition hover:border-border hover:bg-card"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">Queue</p>
                <p className="mt-1 font-serif text-base font-light text-foreground group-hover:text-foreground">
                  Memorial product requests
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Review saved keepsake requests — payment and fulfilment not live yet.
                </p>
              </div>
              <ClipboardList className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            </div>
          </Link>
        </nav>

        {error ? (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        {loading && !data ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : null}

        {data ? (
          <>
            <Section title="System health" icon={Server}>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg border border-border/50 p-3">
                  <p className="text-xs text-muted-foreground">Database</p>
                  <div className="mt-2">
                    <StatusBadge status={health?.database?.status} />
                  </div>
                </div>
                <div className="rounded-lg border border-border/50 p-3">
                  <p className="text-xs text-muted-foreground">Email (Resend)</p>
                  <div className="mt-2">
                    <StatusBadge status={health?.email?.status} />
                  </div>
                </div>
                <div className="rounded-lg border border-border/50 p-3">
                  <p className="text-xs text-muted-foreground">Worker / queue</p>
                  <div className="mt-2">
                    <StatusBadge status={health?.worker?.status} />
                  </div>
                </div>
                <div className="rounded-lg border border-border/50 p-3">
                  <p className="text-xs text-muted-foreground">Storage</p>
                  <p className="mt-2 text-sm font-medium">{storageHealth.storage_mode || health?.storage?.storage_mode || '—'}</p>
                </div>
              </div>
            </Section>

            <Section title="Product overview" icon={BarChart3}>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Users" value={overview.total_users} />
                <StatCard label="Archives" value={overview.total_archives} />
                <StatCard label="People" value={overview.total_people} />
                <StatCard label="Stories" value={overview.total_stories} />
                <StatCard label="Voice memories" value={overview.total_voice_memories} />
                <StatCard label="Documents" value={overview.total_documents} />
                <StatCard label="Memorials" value={overview.total_memorials} hint={`${overview.published_memorials} published`} />
                <StatCard label="Vault items" value={overview.vault_items} />
                <StatCard label="QR links" value={overview.qr_links_created} hint={`${overview.qr_scans_total} scans`} />
                <StatCard label="Photo link previews" value={overview.keepsake_previews} hint={`${overview.keepsake_requests} requests`} />
              </div>
            </Section>

            <Section title="Activation funnel" icon={Activity}>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Registered" value={funnel.registered_users} />
                <StatCard label="First archive" value={funnel.users_with_first_archive} hint={`${conversions.registration_to_archive_pct}% conv.`} />
                <StatCard label="Archives w/ person" value={funnel.archives_with_first_person} hint={`${conversions.archive_to_person_pct}% conv.`} />
                <StatCard label="Users w/ story" value={funnel.users_with_first_story} hint={`${conversions.person_to_story_pct}% conv.`} />
                <StatCard label="Voice memory users" value={funnel.users_with_voice_memory} />
                <StatCard label="Published memorial users" value={funnel.users_with_published_memorial} hint={`${conversions.memorial_created_to_published_pct}% memorial publish`} />
                <StatCard label="Legacy book previews" value={funnel.legacy_book_previews} />
                <StatCard label="Photo link builder opens" value={funnel.memorial_photo_link_builder_opens} />
              </div>
            </Section>

            <Section title="Decision signals" icon={AlertTriangle}>
              {signals.length ? (
                <ul className="space-y-3">
                  {signals.map((signal) => (
                    <li
                      key={signal.message}
                      className="rounded-lg border border-border/50 bg-muted/20 px-4 py-3 text-sm"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="rounded-full text-[10px] uppercase">
                          {signal.severity}
                        </Badge>
                        <p className="font-medium text-foreground">{signal.message}</p>
                      </div>
                      {signal.detail ? <p className="mt-1 text-xs text-muted-foreground">{signal.detail}</p> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No rule-based signals yet — more usage data will populate this section.</p>
              )}
            </Section>

            <Section title="Reliability / failures" icon={AlertTriangle}>
              <div className="mb-4 grid gap-3 sm:grid-cols-4">
                <StatCard label="Failed transcriptions" value={reliability.counts?.failed_transcriptions} />
                <StatCard label="Failed narrations" value={reliability.counts?.failed_narrations} />
                <StatCard label="Failed checkouts" value={reliability.counts?.failed_checkouts} />
                <StatCard label="Failed events" value={reliability.counts?.failed_product_events} />
              </div>
              <SimpleTable
                columns={[
                  { key: 'type', label: 'Type' },
                  { key: 'at', label: 'When' },
                  { key: 'user_id', label: 'User id', render: (r) => (r.user_id ? `${r.user_id.slice(0, 8)}…` : '—') },
                  { key: 'error_summary', label: 'Summary' },
                ]}
                rows={reliability.recent_failures || []}
                emptyMessage="No recent failures logged."
              />
            </Section>

            <Section title="Background jobs" icon={Server}>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Transcription pending" value={jobs.transcription_jobs?.pending} />
                <StatCard label="Processing" value={jobs.transcription_jobs?.processing} />
                <StatCard label="Completed" value={jobs.transcription_jobs?.completed} />
                <StatCard label="Failed" value={jobs.transcription_jobs?.failed} />
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Last success: {jobs.last_successful_transcription_at || '—'} · Oldest pending:{' '}
                {jobs.oldest_pending_transcription_at || '—'}
              </p>
            </Section>

            <Section title="Memorial Bridge" icon={Heart}>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Created" value={memorial.memorials_created} />
                <StatCard label="Published" value={memorial.memorials_published} />
                <StatCard label="QR enabled" value={memorial.memorials_qr_enabled} />
                <StatCard label="QR scans" value={memorial.qr_scans_total} />
                <StatCard label="Tributes pending" value={memorial.tributes_pending} />
                <StatCard label="Photo previews" value={memorial.photo_link_previews} />
                <StatCard label="Photo requests" value={memorial.photo_link_requests} />
              </div>
            </Section>

            <Section title="Email health" icon={Mail}>
              <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                {Object.entries(emailHealth.sent_by_kind || {}).map(([kind, count]) => (
                  <StatCard key={kind} label={`Sent · ${kind}`} value={count} />
                ))}
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                Resend configured: {emailHealth.resend_configured ? 'Yes' : 'No'} · Total sent logged:{' '}
                {emailHealth.total_sent ?? 0}
              </p>
              <SimpleTable
                columns={[
                  { key: 'type', label: 'Kind' },
                  { key: 'at', label: 'When' },
                  { key: 'error_summary', label: 'Summary' },
                ]}
                rows={emailHealth.recent_failed || []}
                emptyMessage="No failed email events logged yet."
              />
            </Section>

            <Section title="Product events" icon={BarChart3}>
              <div className="mb-4 grid gap-3 sm:grid-cols-3">
                <StatCard label="Last 24h" value={events.events_last_24h} />
                <StatCard label={`Period (${period})`} value={events.events_in_period} />
                <StatCard label="Upload failures logged" value={storageHealth.upload_failures_logged} />
              </div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Top features</p>
              {events.top_features?.length ? (
                <ul className="mb-4 space-y-1 text-sm">
                  {events.top_features.map((row) => (
                    <li key={row.event_name} className="flex justify-between gap-4 border-b border-border/30 py-1">
                      <span>{row.event_name}</span>
                      <span className="font-mono text-muted-foreground">{row.count}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mb-4 text-sm text-muted-foreground">No events in this period yet.</p>
              )}
            </Section>
          </>
        ) : null}
      </main>
    </div>
  );
}
